import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

function b64ToUint8(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
}

// Web Push subscription state for the current device. Service worker must be registered (production build).
export function usePushNotifications() {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [status, setStatus] = useState(supported ? "checking" : "unsupported");
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supported) return;
    let alive = true;
    (async () => {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) { if (alive) setStatus("no_worker"); return; }
      const cur = await reg.pushManager.getSubscription();
      if (!alive) return;
      setSubscription(cur);
      setStatus(cur ? "on" : (Notification.permission === "denied" ? "denied" : "off"));
    })().catch(() => alive && setStatus("error"));
    return () => { alive = false; };
  }, [supported]);

  const enable = useCallback(async () => {
    setError(null); setStatus("working");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus("denied"); return false; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.publicKey) });
      await api.post("/push/subscribe", { ...sub.toJSON(), device_label: navigator.userAgent.slice(0, 120) });
      setSubscription(sub); setStatus("on");
      return true;
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Couldn't enable notifications"); setStatus("error");
      return false;
    }
  }, []);

  const disable = useCallback(async () => {
    if (!subscription) return;
    setStatus("working");
    try {
      await api.delete("/push/subscribe", { params: { endpoint: subscription.endpoint } });
      await subscription.unsubscribe();
    } finally { setSubscription(null); setStatus("off"); }
  }, [subscription]);

  return { supported, status, error, enable, disable, isOn: status === "on" };
}
